// Статус пункта на дату отсчёта и группы листа контроля (003: FR-013 – FR-023,
// data-model.md «Статус пункта»). Статус не хранится: зависит от даты отсчёта.
import { z } from '../kernel/zod.ts';
import { ClarifyReasonSchema } from './item.ts';

/** Чем закрыт пункт: решением ИИ по примечанию (004) или отметкой выполнения (010). */
export const closedByKinds = ['evidence', 'mark'] as const;
export type ClosedBy = (typeof closedByKinds)[number];
export const ClosedBySchema = z.enum(closedByKinds);

/** Статусы среза. Жёлтый («выполнено со слов») появится с ответами. */
export const itemStatuses = ['none', 'red', 'black', 'clarify'] as const;
export type ItemStatus = (typeof itemStatuses)[number];
export const ItemStatusSchema = z.enum(itemStatuses);

/** Подпись бейджа и печати — по словарю, раздел «Статусы пункта». */
export const itemStatusLabels = {
  none: 'Без цвета',
  red: 'Красный',
  black: 'Чёрный',
  clarify: 'Уточнить',
} as const satisfies Record<ItemStatus, string>;

export const ItemStatusInfoSchema = z.object({
  status: ItemStatusSchema,
  /** Красный с прошедшим сроком (FR-015). */
  overdue: z.boolean(),
  /** На паузе: не красный и не просрочен (FR-016). */
  paused: z.boolean(),
  /** Причина у статуса «Уточнить». */
  clarify: ClarifyReasonSchema.nullable(),
  /**
   * Чем закрыт чёрный пункт: evidence — свидетельство выполнения, решение ИИ не ниже порога
   * (004, FR-003, FR-008); mark — отметка выполнения правилом (010, FR-047).
   */
  closedBy: ClosedBySchema.nullable().optional(),
  /** Вероятность свидетельства, если модель спрашивали (004). */
  evidenceProbability: z.number().min(0).max(1).nullable().optional(),
  /**
   * Свидетельство неуверенное: вероятность в полосе проверки (больше evidenceReviewFrom и не больше
   * порога) — человек подтверждает на планёрке; статус по сроку не меняется (004, решение
   * владельца продукта «спорное — на контролёра»).
   */
  evidenceReview: z.boolean().optional(),
});
export type ItemStatusInfo = z.infer<typeof ItemStatusInfoSchema>;

/** Параметры политики протокола планёрки — из настроек установки (FR-018). */
export const MeetingPolicySchema = z.object({
  /** Красный — до срока столько дней или меньше. */
  redDays: z.number().int().min(0).max(90),
  /**
   * Порог уверенности свидетельства выполнения (004, FR-004): решение владельца продукта — 0,9,
   * записан в config/defaults.yaml; нет поля — EVIDENCE_THRESHOLD_DEFAULT.
   */
  evidenceThreshold: z.number().min(0.5).max(1).optional(),
  /** Нижняя граница полосы проверки свидетельства; нет поля — EVIDENCE_REVIEW_FROM_DEFAULT. */
  evidenceReviewFrom: z.number().min(0).max(1).optional(),
});
export const EVIDENCE_THRESHOLD_DEFAULT = 0.9;
export const EVIDENCE_REVIEW_FROM_DEFAULT = 0.7;
export type MeetingPolicy = z.infer<typeof MeetingPolicySchema>;

/**
 * Группы листа контроля в порядке печати; пункт — в одной группе (FR-019). «Проверить
 * выполнение (ИИ)» и «Закрыто по примечанию (ИИ)» — последние: решение ИИ видно человеку
 * (004, FR-009; принцип III). Неуверенное свидетельство — в «Проверить», а не в «Просрочено».
 */
export const controlSheetGroups = [
  'overdue',
  'burning',
  'clarify',
  'transfers',
  'aiReview',
  'aiClosed',
] as const;
export type ControlSheetGroup = (typeof controlSheetGroups)[number];
export const ControlSheetGroupSchema = z.enum(controlSheetGroups);

export const controlSheetGroupLabels = {
  overdue: 'Просрочено',
  burning: 'Горит',
  clarify: 'Неясно',
  transfers: 'Переносы',
  aiReview: 'Проверить выполнение (ИИ)',
  aiClosed: 'Закрыто по примечанию (ИИ)',
} as const satisfies Record<ControlSheetGroup, string>;
